import React, { useState, useMemo } from 'react';
import {
    AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
    FaChartLine, FaCalendarAlt, FaUserMd, FaTooth, FaEuroSign,
    FaArrowUp, FaArrowDown, FaBoxes, FaFileInvoiceDollar,
    FaEye, FaEyeSlash
} from 'react-icons/fa';
import '../styles/RevenueAnalytics.css';

// Palette de couleurs pour les catégories et graphiques
const CATEGORY_COLORS = {
    'Zircone & Tout-Céramique': '#0ea5e9', // Sky blue
    'Stellite / CCP & Métal': '#8b5cf6',   // Purple
    'Inlay-Core & Couronne Coulée': '#f59e0b', // Amber
    'CCM & Provisoires': '#10b981',        // Emerald
    'Implantologie': '#ec4899',            // Pink
    'Prothèse Adjointe Résine': '#6366f1', // Indigo
    'Gouttières': '#14b8a6',               // Teal
    'Divers & Réparations': '#94a3b8'      // Slate
};

const PALETTE = ['#0ea5e9', '#8b5cf6', '#10b981', '#f59e0b', '#ec4899', '#6366f1', '#14b8a6', '#94a3b8', '#f97316', '#06b6d4'];

const MONTH_NAMES = ['Janv.', 'Févr.', 'Mars', 'Avr.', 'Mai', 'Juin', 'Juil.', 'Août', 'Sept.', 'Oct.', 'Nov.', 'Déc.'];
const MONTH_FULL = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

/**
 * Normalise la catégorie d'un item / produit pour le regroupement statistique
 */
const normalizeProsthesisCategory = (rawCategory = '', rawName = '') => {
    const cat = (rawCategory || '').toLowerCase();
    const name = (rawName || '').toLowerCase();

    if (cat.includes('zircone') || cat.includes('céramique') || cat.includes('ceramique') || cat.includes('cfao') || name.includes('zircone') || name.includes('emax')) {
        if (cat.includes('implant') || name.includes('sur implant')) return 'Implantologie';
        return 'Zircone & Tout-Céramique';
    }
    if (cat.includes('stellite') || cat.includes('ccp') || cat.includes('châssis') || cat.includes('chassis') || name.includes('stellite') || name.includes('ccp')) {
        return 'Stellite / CCP & Métal';
    }
    if (cat.includes('inlay core') || cat.includes('coulée') || cat.includes('coulee') || name.includes('couronne coulée') || name.includes('inlay core')) {
        return 'Inlay-Core & Couronne Coulée';
    }
    if (cat.includes('ccm') || cat.includes('provisoire') || name.includes('ccm') || name.includes('céramique sur métal') || name.includes('provisoire')) {
        return 'CCM & Provisoires';
    }
    if (cat.includes('implant') || name.includes('pilier') || name.includes('barre titane') || name.includes('all on')) {
        return 'Implantologie';
    }
    if (cat.includes('gouttière') || cat.includes('gouttiere') || name.includes('gouttière') || name.includes('bruxisme') || name.includes('blanchiment')) {
        return 'Gouttières';
    }
    if (cat.includes('partielle') || cat.includes('complète') || cat.includes('complete') || cat.includes('adjointe') || name.includes('prothèse') || name.includes('appareil')) {
        return 'Prothèse Adjointe Résine';
    }
    if (cat.includes('divers') || cat.includes('attachement') || cat.includes('réparation') || cat.includes('reparation')) {
        return 'Divers & Réparations';
    }

    return 'Zircone & Tout-Céramique';
};

const RevenueAnalytics = ({ invoices = [], orders = [], clients = [], products = [] }) => {
    const [periodFilter, setPeriodFilter] = useState('all'); // 'all', 'current-year', 'last-12-months', or specific year '2026'
    const [selectedPractitioner, setSelectedPractitioner] = useState('all');
    const [chartMetric, setChartMetric] = useState('revenue'); // 'revenue', 'count', 'average'
    const [hideFinancials, setHideFinancials] = useState(() => {
        return localStorage.getItem('enovadent_hide_financials') === 'true';
    });

    const toggleHideFinancials = () => {
        setHideFinancials(prev => {
            const next = !prev;
            localStorage.setItem('enovadent_hide_financials', String(next));
            return next;
        });
    };

    // Obtenir la liste des années disponibles dans les factures
    const availableYears = useMemo(() => {
        const yearsSet = new Set();
        invoices.forEach(inv => {
            const d = new Date(inv.date || inv.createdAt || inv.created_at);
            if (!isNaN(d.getFullYear())) {
                yearsSet.add(d.getFullYear());
            }
        });
        if (yearsSet.size === 0) yearsSet.add(new Date().getFullYear());
        return Array.from(yearsSet).sort((a, b) => b - a);
    }, [invoices]);

    // Filtrer les factures selon la période et le praticien
    const filteredInvoices = useMemo(() => {
        const now = new Date();
        const currentYear = now.getFullYear();

        return invoices.filter(inv => {
            const d = new Date(inv.date || inv.createdAt || inv.created_at);
            if (isNaN(d.getTime())) return false;

            // Filtre praticien
            if (selectedPractitioner !== 'all') {
                const matchId = inv.clientId === selectedPractitioner || inv.client_id === selectedPractitioner;
                const matchName = inv.practitionerName === selectedPractitioner || inv.practitioner_name === selectedPractitioner;
                if (!matchId && !matchName) return false;
            }

            // Filtre période
            if (periodFilter === 'current-year') {
                return d.getFullYear() === currentYear;
            } else if (periodFilter === 'last-12-months') {
                const twelveMonthsAgo = new Date();
                twelveMonthsAgo.setMonth(now.getMonth() - 11);
                twelveMonthsAgo.setDate(1);
                twelveMonthsAgo.setHours(0, 0, 0, 0);
                return d >= twelveMonthsAgo;
            } else if (periodFilter === 'last-6-months') {
                const sixMonthsAgo = new Date();
                sixMonthsAgo.setMonth(now.getMonth() - 5);
                sixMonthsAgo.setDate(1);
                sixMonthsAgo.setHours(0, 0, 0, 0);
                return d >= sixMonthsAgo;
            } else if (periodFilter !== 'all') {
                // Année spécifique
                return d.getFullYear() === parseInt(periodFilter, 10);
            }

            return true;
        });
    }, [invoices, periodFilter, selectedPractitioner]);

    // Dictionnaire d'ordres pour enrichir les items si manquants
    const ordersMap = useMemo(() => {
        const map = new Map();
        orders.forEach(o => {
            if (o.id) map.set(o.id, o);
        });
        return map;
    }, [orders]);

    // Calcul des statistiques globales (KPIs)
    const kpis = useMemo(() => {
        const totalRevenue = filteredInvoices.reduce((sum, inv) => {
            return sum + (parseFloat(inv.amount || inv.total_amount || inv.total || 0) || 0);
        }, 0);

        const totalInvoices = filteredInvoices.length;
        const averageTicket = totalInvoices > 0 ? totalRevenue / totalInvoices : 0;

        // Calcul comparatif sur la période précédente
        const monthlySums = {};
        filteredInvoices.forEach(inv => {
            const d = new Date(inv.date || inv.createdAt || inv.created_at);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            const amount = parseFloat(inv.amount || inv.total_amount || inv.total || 0) || 0;
            monthlySums[key] = (monthlySums[key] || 0) + amount;
        });

        const sortedMonthKeys = Object.keys(monthlySums).sort();
        let growthRate = null;
        if (sortedMonthKeys.length >= 2) {
            const lastMonthKey = sortedMonthKeys[sortedMonthKeys.length - 1];
            const prevMonthKey = sortedMonthKeys[sortedMonthKeys.length - 2];
            const lastAmount = monthlySums[lastMonthKey] || 0;
            const prevAmount = monthlySums[prevMonthKey] || 0;
            if (prevAmount > 0) {
                growthRate = ((lastAmount - prevAmount) / prevAmount) * 100;
            }
        }

        return {
            totalRevenue,
            totalInvoices,
            averageTicket,
            growthRate
        };
    }, [filteredInvoices]);

    // Évolution mensuelle (Série temporelle pour Recharts)
    const monthlyData = useMemo(() => {
        const map = new Map();

        // Si "last-12-months" ou "last-6-months", initialiser tous les mois de la plage
        if (periodFilter === 'last-12-months' || periodFilter === 'last-6-months') {
            const count = periodFilter === 'last-12-months' ? 12 : 6;
            const now = new Date();
            for (let i = count - 1; i >= 0; i--) {
                const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                map.set(key, {
                    key,
                    year: d.getFullYear(),
                    monthIndex: d.getMonth(),
                    monthLabel: `${MONTH_NAMES[d.getMonth()]} ${String(d.getFullYear()).slice(-2)}`,
                    fullLabel: `${MONTH_FULL[d.getMonth()]} ${d.getFullYear()}`,
                    revenue: 0,
                    count: 0,
                    categoryBreakdown: {}
                });
            }
        } else if (periodFilter !== 'all') {
            // Année complète de Janvier à Décembre
            const year = periodFilter === 'current-year' ? new Date().getFullYear() : parseInt(periodFilter, 10);
            for (let m = 0; m < 12; m++) {
                const key = `${year}-${String(m + 1).padStart(2, '0')}`;
                map.set(key, {
                    key,
                    year,
                    monthIndex: m,
                    monthLabel: MONTH_NAMES[m],
                    fullLabel: `${MONTH_FULL[m]} ${year}`,
                    revenue: 0,
                    count: 0,
                    categoryBreakdown: {}
                });
            }
        }

        // Remplir avec les données des factures
        filteredInvoices.forEach(inv => {
            const d = new Date(inv.date || inv.createdAt || inv.created_at);
            const year = d.getFullYear();
            const month = d.getMonth();
            const key = `${year}-${String(month + 1).padStart(2, '0')}`;
            const amount = parseFloat(inv.amount || inv.total_amount || inv.total || 0) || 0;

            if (!map.has(key)) {
                map.set(key, {
                    key,
                    year,
                    monthIndex: month,
                    monthLabel: `${MONTH_NAMES[month]} ${String(year).slice(-2)}`,
                    fullLabel: `${MONTH_FULL[month]} ${year}`,
                    revenue: 0,
                    count: 0,
                    categoryBreakdown: {}
                });
            }

            const entry = map.get(key);
            entry.revenue += amount;
            entry.count += 1;

            // Extraire la catégorie dominante pour le mois
            let items = Array.isArray(inv.items) ? inv.items : [];
            if (items.length === 0 && inv.orderId && ordersMap.has(inv.orderId)) {
                const order = ordersMap.get(inv.orderId);
                if (order.workType) {
                    items = [{ name: order.workType, category: order.workType }];
                }
            }

            items.forEach(it => {
                const cat = normalizeProsthesisCategory(it.category, it.name || it.description);
                entry.categoryBreakdown[cat] = (entry.categoryBreakdown[cat] || 0) + (parseFloat(it.price || amount) || 0);
            });
        });

        // Convertir en tableau trié chronologiquement
        const sorted = Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));

        // Calculer les moyennes et évolutions M-1
        let prevRevenue = null;
        return sorted.map(item => {
            const average = item.count > 0 ? Math.round(item.revenue / item.count) : 0;
            let growth = null;
            if (prevRevenue !== null && prevRevenue > 0) {
                growth = Math.round(((item.revenue - prevRevenue) / prevRevenue) * 100);
            }
            prevRevenue = item.revenue;

            // Trouver la top catégorie du mois
            let topCat = '-';
            let maxCatRev = 0;
            Object.entries(item.categoryBreakdown).forEach(([cat, rev]) => {
                if (rev > maxCatRev) {
                    maxCatRev = rev;
                    topCat = cat;
                }
            });

            return {
                ...item,
                revenue: Math.round(item.revenue * 100) / 100,
                average,
                growth,
                topCat
            };
        });
    }, [filteredInvoices, periodFilter, ordersMap]);

    // Répartition par Catégorie de Prothèse
    const categoryData = useMemo(() => {
        const catMap = {};

        filteredInvoices.forEach(inv => {
            const invoiceAmount = parseFloat(inv.amount || inv.total_amount || inv.total || 0) || 0;
            let items = Array.isArray(inv.items) && inv.items.length > 0 ? inv.items : null;

            // Si pas d'items, récupérer depuis l'ordre
            if (!items && inv.orderId && ordersMap.has(inv.orderId)) {
                const order = ordersMap.get(inv.orderId);
                if (order.workType) {
                    items = [{ name: order.workType, category: order.workType, price: invoiceAmount, quantity: 1 }];
                }
            }

            if (items && items.length > 0) {
                items.forEach(it => {
                    const normCat = normalizeProsthesisCategory(it.category, it.name || it.description);
                    const itemAmount = parseFloat(it.price || it.unitPrice || (invoiceAmount / items.length)) || 0;
                    const qty = parseInt(it.quantity || 1, 10);

                    if (!catMap[normCat]) {
                        catMap[normCat] = { name: normCat, value: 0, count: 0 };
                    }
                    catMap[normCat].value += itemAmount;
                    catMap[normCat].count += qty;
                });
            } else {
                const defaultCat = 'Zircone & Tout-Céramique';
                if (!catMap[defaultCat]) {
                    catMap[defaultCat] = { name: defaultCat, value: 0, count: 0 };
                }
                catMap[defaultCat].value += invoiceAmount;
                catMap[defaultCat].count += 1;
            }
        });

        const totalCatRev = Object.values(catMap).reduce((sum, c) => sum + c.value, 0);

        return Object.values(catMap)
            .map(c => ({
                ...c,
                value: Math.round(c.value * 100) / 100,
                percentage: totalCatRev > 0 ? Math.round((c.value / totalCatRev) * 100) : 0,
                color: CATEGORY_COLORS[c.name] || '#64748b'
            }))
            .sort((a, b) => b.value - a.value);
    }, [filteredInvoices, ordersMap]);

    // Répartition par Praticien / Client (Top prescripteurs)
    const practitionerData = useMemo(() => {
        const clientMap = {};

        filteredInvoices.forEach(inv => {
            const amount = parseFloat(inv.amount || inv.total_amount || inv.total || 0) || 0;
            const clientId = inv.clientId || inv.client_id;
            const clientObj = clients.find(c => c.id === clientId);
            const clientName = clientObj?.name || inv.practitionerName || inv.practitioner_name || 'Client Inconnu';
            const clinic = clientObj?.clinic || clientObj?.address || '';

            const key = clientId || clientName;

            if (!clientMap[key]) {
                clientMap[key] = {
                    id: key,
                    name: clientName.startsWith('Dr') ? clientName : `Dr. ${clientName}`,
                    clinic,
                    revenue: 0,
                    count: 0
                };
            }

            clientMap[key].revenue += amount;
            clientMap[key].count += 1;
        });

        const totalRev = kpis.totalRevenue;

        return Object.values(clientMap)
            .map(c => ({
                ...c,
                revenue: Math.round(c.revenue * 100) / 100,
                average: c.count > 0 ? Math.round(c.revenue / c.count) : 0,
                percentage: totalRev > 0 ? Math.round((c.revenue / totalRev) * 100) : 0
            }))
            .sort((a, b) => b.revenue - a.revenue);
    }, [filteredInvoices, clients, kpis.totalRevenue]);

    // Custom Tooltip pour Recharts
    const CustomAreaTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="analytics-custom-tooltip shadow-lg">
                    <div className="tooltip-header">{data.fullLabel || label}</div>
                    <div className="tooltip-row">
                        <span className="tooltip-label">Chiffre d'Affaires :</span>
                        <span className="tooltip-val fw-bold text-primary">
                            {hideFinancials ? '•••••• €' : `${data.revenue.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`}
                        </span>
                    </div>
                    <div className="tooltip-row">
                        <span className="tooltip-label">Dossiers facturés :</span>
                        <span className="tooltip-val fw-bold">{data.count}</span>
                    </div>
                    <div className="tooltip-row">
                        <span className="tooltip-label">Panier moyen :</span>
                        <span className="tooltip-val">
                            {hideFinancials ? '•••• €' : `${data.average.toLocaleString('fr-FR')} €`}
                        </span>
                    </div>
                    {data.growth !== null && (
                        <div className="tooltip-row mt-1 pt-1 border-top">
                            <span className="tooltip-label">Évolution M-1 :</span>
                            <span className={`tooltip-val fw-bold ${data.growth >= 0 ? 'text-success' : 'text-danger'}`}>
                                {data.growth >= 0 ? `+${data.growth}%` : `${data.growth}%`}
                            </span>
                        </div>
                    )}
                </div>
            );
        }
        return null;
    };

    const CustomPieTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="analytics-custom-tooltip shadow-lg">
                    <div className="tooltip-header" style={{ color: data.color }}>{data.name}</div>
                    <div className="tooltip-row">
                        <span className="tooltip-label">CA généré :</span>
                        <span className="tooltip-val fw-bold">
                            {hideFinancials ? '•••••• €' : `${data.value.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`}
                        </span>
                    </div>
                    <div className="tooltip-row">
                        <span className="tooltip-label">Part du CA :</span>
                        <span className="tooltip-val fw-bold text-info">{data.percentage}%</span>
                    </div>
                    <div className="tooltip-row">
                        <span className="tooltip-label">Unités produites :</span>
                        <span className="tooltip-val">{data.count} pièces</span>
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="revenue-analytics-container animate__animated animate__fadeIn">
            {/* Header des Statistiques & Filtres */}
            <div className="analytics-control-bar card border-0 shadow-sm p-3 mb-4 rounded-4">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                    <div className="d-flex align-items-center gap-3">
                        <div className="analytics-icon-box">
                            <FaChartLine size={22} />
                        </div>
                        <div>
                            <h4 className="fw-bold mb-0 text-slate-800">Pilotage & Évolution du Chiffre d'Affaires</h4>
                            <p className="text-muted small mb-0">Analyse détaillée des performances, des prothèses et des partenariats cabinets.</p>
                        </div>
                    </div>

                    <div className="d-flex flex-wrap align-items-center gap-2">
                        {/* Filtre Période */}
                        <div className="filter-pill-select">
                            <FaCalendarAlt className="me-2 text-primary" size={14} />
                            <select
                                value={periodFilter}
                                onChange={(e) => setPeriodFilter(e.target.value)}
                                className="form-select form-select-sm border-0 bg-transparent fw-semibold"
                            >
                                <option value="all">Tout l'historique</option>
                                <option value="current-year">Année en cours ({new Date().getFullYear()})</option>
                                <option value="last-12-months">12 derniers mois</option>
                                <option value="last-6-months">6 derniers mois</option>
                                {availableYears.map(yr => (
                                    <option key={yr} value={String(yr)}>Année {yr}</option>
                                ))}
                            </select>
                        </div>

                        {/* Filtre Praticien */}
                        <div className="filter-pill-select">
                            <FaUserMd className="me-2 text-indigo" size={14} />
                            <select
                                value={selectedPractitioner}
                                onChange={(e) => setSelectedPractitioner(e.target.value)}
                                className="form-select form-select-sm border-0 bg-transparent fw-semibold"
                            >
                                <option value="all">Tous les praticiens</option>
                                {clients.map(cl => (
                                    <option key={cl.id} value={cl.id}>
                                        {cl.name.startsWith('Dr') ? cl.name : `Dr. ${cl.name}`}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Bouton de Masquage / Confidentialité */}
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary rounded-3 d-flex align-items-center gap-1"
                            onClick={toggleHideFinancials}
                            title={hideFinancials ? "Afficher les montants" : "Masquer les montants"}
                        >
                            {hideFinancials ? <FaEyeSlash /> : <FaEye />}
                            <span className="d-none d-sm-inline">{hideFinancials ? 'Démasquer' : 'Confidentialité'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="analytics-kpi-grid mb-4">
                {/* KPI 1: CA Total */}
                <div className="analytics-kpi-card revenue-card">
                    <div className="kpi-header">
                        <span className="kpi-label">Chiffre d'Affaires Période</span>
                        <div className="kpi-icon"><FaFileInvoiceDollar /></div>
                    </div>
                    <div className={`kpi-value ${hideFinancials ? 'blurred-val' : ''}`}>
                        {hideFinancials ? '•••••• €' : `${kpis.totalRevenue.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`}
                    </div>
                    <div className="kpi-footer">
                        {kpis.growthRate !== null ? (
                            <span className={`kpi-badge ${kpis.growthRate >= 0 ? 'positive' : 'negative'}`}>
                                {kpis.growthRate >= 0 ? <FaArrowUp /> : <FaArrowDown />}
                                {Math.abs(Math.round(kpis.growthRate))}% vs mois précédent
                            </span>
                        ) : (
                            <span className="text-muted small">Sur {filteredInvoices.length} factures</span>
                        )}
                    </div>
                </div>

                {/* KPI 2: Dossiers Traités */}
                <div className="analytics-kpi-card orders-card">
                    <div className="kpi-header">
                        <span className="kpi-label">Dossiers & Travaux Facturés</span>
                        <div className="kpi-icon"><FaBoxes /></div>
                    </div>
                    <div className="kpi-value">
                        {kpis.totalInvoices} <span className="kpi-unit">dossiers</span>
                    </div>
                    <div className="kpi-footer">
                        <span className="text-muted small">
                            {practitionerData.length} praticiens actifs
                        </span>
                    </div>
                </div>

                {/* KPI 3: Panier Moyen */}
                <div className="analytics-kpi-card basket-card">
                    <div className="kpi-header">
                        <span className="kpi-label">Panier Moyen par Travail</span>
                        <div className="kpi-icon"><FaEuroSign /></div>
                    </div>
                    <div className={`kpi-value ${hideFinancials ? 'blurred-val' : ''}`}>
                        {hideFinancials ? '•••• €' : `${kpis.averageTicket.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`}
                    </div>
                    <div className="kpi-footer">
                        <span className="text-muted small">Valeur moyenne unitaire</span>
                    </div>
                </div>

                {/* KPI 4: Top Catégorie */}
                <div className="analytics-kpi-card category-card">
                    <div className="kpi-header">
                        <span className="kpi-label">Prothèse N°1 (Volume CA)</span>
                        <div className="kpi-icon"><FaTooth /></div>
                    </div>
                    <div className="kpi-value text-truncate" style={{ fontSize: '1.25rem' }}>
                        {categoryData[0]?.name || 'Zircone & CFAO'}
                    </div>
                    <div className="kpi-footer">
                        <span className="badge bg-light text-primary border fw-semibold">
                            {categoryData[0]?.percentage || 0}% du CA global
                        </span>
                    </div>
                </div>
            </div>

            {/* Section Graphique 1 : Évolution du CA au fil du temps */}
            <div className="card border-0 shadow-sm rounded-4 p-4 mb-4 bg-white">
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
                    <div>
                        <h5 className="fw-bold mb-1 d-flex align-items-center gap-2">
                            <FaChartLine className="text-primary" /> Évolution du Chiffre d'Affaires & Tendance
                        </h5>
                        <p className="text-muted small mb-0">Progression mensuelle du laboratoire et volume d'activité.</p>
                    </div>

                    <div className="btn-group btn-group-sm" role="group">
                        <button
                            type="button"
                            className={`btn ${chartMetric === 'revenue' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setChartMetric('revenue')}
                        >
                            Chiffre d'Affaires (€)
                        </button>
                        <button
                            type="button"
                            className={`btn ${chartMetric === 'count' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setChartMetric('count')}
                        >
                            Nb Dossiers
                        </button>
                        <button
                            type="button"
                            className={`btn ${chartMetric === 'average' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setChartMetric('average')}
                        >
                            Panier Moyen
                        </button>
                    </div>
                </div>

                <div style={{ width: '100%', height: 340 }}>
                    {monthlyData.length === 0 ? (
                        <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                            Aucune donnée disponible pour cette sélection.
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={monthlyData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.4} />
                                        <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0.0} />
                                    </linearGradient>
                                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.4} />
                                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                <XAxis dataKey="monthLabel" stroke="#94a3b8" fontSize={12} tickLine={false} />
                                <YAxis
                                    stroke="#94a3b8"
                                    fontSize={12}
                                    tickLine={false}
                                    tickFormatter={(val) => hideFinancials ? '••' : (chartMetric === 'revenue' || chartMetric === 'average' ? `${val}€` : val)}
                                />
                                <Tooltip content={<CustomAreaTooltip />} />
                                {chartMetric === 'revenue' && (
                                    <Area
                                        type="monotone"
                                        dataKey="revenue"
                                        name="Chiffre d'Affaires"
                                        stroke="#0ea5e9"
                                        strokeWidth={3}
                                        fillOpacity={1}
                                        fill="url(#colorRevenue)"
                                    />
                                )}
                                {chartMetric === 'count' && (
                                    <Area
                                        type="monotone"
                                        dataKey="count"
                                        name="Nombre de dossiers"
                                        stroke="#8b5cf6"
                                        strokeWidth={3}
                                        fillOpacity={1}
                                        fill="url(#colorCount)"
                                    />
                                )}
                                {chartMetric === 'average' && (
                                    <Area
                                        type="monotone"
                                        dataKey="average"
                                        name="Panier Moyen"
                                        stroke="#10b981"
                                        strokeWidth={3}
                                        fillOpacity={1}
                                        fill="url(#colorRevenue)"
                                    />
                                )}
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>

            {/* Ligne 2 Colonnes : Répartition Prothèses + Top Praticiens */}
            <div className="row g-4 mb-4">
                {/* Répartition par Catégorie de Prothèse */}
                <div className="col-lg-6">
                    <div className="card border-0 shadow-sm rounded-4 p-4 h-100 bg-white">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="fw-bold mb-0 d-flex align-items-center gap-2">
                                <FaTooth className="text-primary" /> Répartition par Type de Prothèse
                            </h5>
                            <span className="badge bg-light text-secondary border">Mix Produits</span>
                        </div>
                        <p className="text-muted small mb-3">Part des différentes familles dans le chiffre d'affaires total.</p>

                        <div style={{ width: '100%', height: 260 }}>
                            {categoryData.length === 0 ? (
                                <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                                    Aucune donnée.
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={categoryData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={65}
                                            outerRadius={95}
                                            paddingAngle={3}
                                            dataKey="value"
                                        >
                                            {categoryData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color || PALETTE[index % PALETTE.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip content={<CustomPieTooltip />} />
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                        {/* Liste ordonnée des catégories */}
                        <div className="category-list-breakdown mt-2">
                            {categoryData.map((cat) => (
                                <div key={cat.name} className="category-breakdown-row">
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="color-dot" style={{ backgroundColor: cat.color }}></span>
                                        <span className="cat-name">{cat.name}</span>
                                    </div>
                                    <div className="d-flex align-items-center gap-3">
                                        <span className="cat-qty text-muted small">{cat.count} pcs</span>
                                        <span className={`cat-amount fw-bold ${hideFinancials ? 'blurred-val' : ''}`}>
                                            {hideFinancials ? '•••• €' : `${cat.value.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`}
                                        </span>
                                        <span className="badge bg-light text-dark border px-2">
                                            {cat.percentage}%
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Classement & Top Praticiens Prescripteurs */}
                <div className="col-lg-6">
                    <div className="card border-0 shadow-sm rounded-4 p-4 h-100 bg-white">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="fw-bold mb-0 d-flex align-items-center gap-2">
                                <FaUserMd className="text-indigo" /> Top Praticiens Partenaires
                            </h5>
                            <span className="badge bg-light text-secondary border">Prescripteurs</span>
                        </div>
                        <p className="text-muted small mb-3">Classement des chirurgiens-dentistes par chiffre d'affaires apporté.</p>

                        {/* Mini BarChart horizontal des Top 5 */}
                        <div style={{ width: '100%', height: 180 }}>
                            {practitionerData.length === 0 ? (
                                <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                                    Aucune donnée.
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        layout="vertical"
                                        data={practitionerData.slice(0, 5)}
                                        margin={{ top: 0, right: 20, left: 40, bottom: 0 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                                        <XAxis type="number" hide />
                                        <YAxis dataKey="name" type="category" stroke="#64748b" fontSize={11} width={90} tickLine={false} />
                                        <Tooltip
                                            formatter={(value) => hideFinancials ? '•••••• €' : `${value.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`}
                                        />
                                        <Bar dataKey="revenue" fill="#6366f1" radius={[0, 8, 8, 0]} barSize={16} />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                        {/* Table des Praticiens */}
                        <div className="table-responsive mt-3" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                            <table className="table table-sm table-hover align-middle mb-0">
                                <thead>
                                    <tr className="text-muted small border-bottom">
                                        <th>Praticien</th>
                                        <th className="text-center">Dossiers</th>
                                        <th className="text-end">CA Généré</th>
                                        <th className="text-end">Part</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {practitionerData.map((doc, idx) => (
                                        <tr
                                            key={doc.id}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setSelectedPractitioner(doc.id)}
                                            className={selectedPractitioner === doc.id ? 'table-primary' : ''}
                                            title="Cliquer pour filtrer les statistiques sur ce praticien"
                                        >
                                            <td>
                                                <div className="fw-semibold text-slate-800">
                                                    <span className="badge bg-light text-secondary me-2 border">#{idx + 1}</span>
                                                    {doc.name}
                                                </div>
                                                {doc.clinic && <div className="text-muted small text-truncate" style={{ maxWidth: '160px' }}>{doc.clinic}</div>}
                                            </td>
                                            <td className="text-center fw-medium">{doc.count}</td>
                                            <td className={`text-end fw-bold text-slate-900 ${hideFinancials ? 'blurred-val' : ''}`}>
                                                {hideFinancials ? '•••• €' : `${doc.revenue.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`}
                                            </td>
                                            <td className="text-end">
                                                <span className="badge bg-light text-primary border">
                                                    {doc.percentage}%
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tableau Récapitulatif Mois par Mois */}
            <div className="card border-0 shadow-sm rounded-4 p-4 bg-white">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0 d-flex align-items-center gap-2">
                        <FaCalendarAlt className="text-primary" /> Historique & Tableau Mensuel Détaillé
                    </h5>
                    <span className="text-muted small">{monthlyData.length} mois répertoriés</span>
                </div>

                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="bg-light">
                            <tr className="text-muted small">
                                <th>Période</th>
                                <th className="text-end">Chiffre d'Affaires</th>
                                <th className="text-center">Dossiers Facturés</th>
                                <th className="text-end">Panier Moyen</th>
                                <th className="text-center">Évolution vs M-1</th>
                                <th>Catégorie dominante</th>
                            </tr>
                        </thead>
                        <tbody>
                            {monthlyData.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-4 text-muted">
                                        Aucun mois enregistré dans cette période.
                                    </td>
                                </tr>
                            ) : (
                                monthlyData.slice().reverse().map((m) => (
                                    <tr key={m.key}>
                                        <td className="fw-bold text-slate-800">{m.fullLabel}</td>
                                        <td className={`text-end fw-bold text-primary ${hideFinancials ? 'blurred-val' : ''}`}>
                                            {hideFinancials ? '•••••• €' : `${m.revenue.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`}
                                        </td>
                                        <td className="text-center">
                                            <span className="badge bg-secondary-subtle text-dark border px-2 py-1">
                                                {m.count} cas
                                            </span>
                                        </td>
                                        <td className={`text-end ${hideFinancials ? 'blurred-val' : ''}`}>
                                            {hideFinancials ? '•••• €' : `${m.average.toLocaleString('fr-FR')} €`}
                                        </td>
                                        <td className="text-center">
                                            {m.growth !== null ? (
                                                <span className={`badge ${m.growth >= 0 ? 'bg-success-subtle text-success border border-success' : 'bg-danger-subtle text-danger border border-danger'}`}>
                                                    {m.growth >= 0 ? `+${m.growth}%` : `${m.growth}%`}
                                                </span>
                                            ) : (
                                                <span className="text-muted small">-</span>
                                            )}
                                        </td>
                                        <td>
                                            <span className="badge bg-light text-dark border">
                                                {m.topCat}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default RevenueAnalytics;
